<template>
  <div class="feedback-module">
    <!-- 页面标题和返回按钮 -->
    <div class="mb-6">
      <div class="flex items-center justify-between">
        <div>
          <h2 class="text-2xl font-bold text-gray-900">问题反馈管理</h2>
          <p class="text-gray-600 mt-2">管理用户提交的问题反馈</p>
        </div>
        <div>
          <router-link
            to="/chat"
            class="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors duration-200"
          >
            <i class="fas fa-arrow-left mr-2"></i>
            返回对话
          </router-link>
        </div>
      </div>
    </div>

    <!-- 统计信息 -->
    <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
      <div class="bg-white p-4 rounded-lg shadow">
        <div class="flex items-center">
          <div class="p-2 bg-blue-100 rounded-lg">
            <i class="fas fa-question text-blue-600"></i>
          </div>
          <div class="ml-4">
            <p class="text-sm text-gray-600">总反馈数</p>
            <p class="text-2xl font-bold text-gray-900">{{ totalFeedbacks }}</p>
          </div>
        </div>
      </div>
      
      <div class="bg-white p-4 rounded-lg shadow">
        <div class="flex items-center">
          <div class="p-2 bg-yellow-100 rounded-lg">
            <i class="fas fa-clock text-yellow-600"></i>
          </div>
          <div class="ml-4">
            <p class="text-sm text-gray-600">未处理</p>
            <p class="text-2xl font-bold text-gray-900">{{ unrepliedCount }}</p>
          </div>
        </div>
      </div>
      
      <div class="bg-white p-4 rounded-lg shadow">
        <div class="flex items-center">
          <div class="p-2 bg-green-100 rounded-lg">
            <i class="fas fa-check text-green-600"></i>
          </div>
          <div class="ml-4">
            <p class="text-sm text-gray-600">已处理</p>
            <p class="text-2xl font-bold text-gray-900">{{ repliedCount }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- 筛选和搜索 -->
    <div class="bg-white p-4 rounded-lg shadow mb-6">
      <div class="flex flex-col md:flex-row gap-4">
        <div class="flex-1">
          <input
            v-model="searchKeyword"
            type="text"
            placeholder="搜索问题内容..."
            class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            @input="searchFeedbacks"
          />
        </div>
        <div class="flex gap-2">
          <select
            v-model="statusFilter"
            @change="filterByStatus"
            class="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">全部状态</option>
            <option value="0">未处理</option>
            <option value="1">已处理</option>
            <option value="2">仅反馈用户</option>
            <option value="3">已撤回</option>
            <option value="4">已退回</option>
            <option value="5">已反馈知识库</option>
          </select>
          <button
            @click="refreshFeedbacks"
            class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors duration-200"
          >
            <i class="fas fa-sync-alt mr-2"></i>刷新
          </button>
        </div>
      </div>
    </div>

    <!-- 反馈列表 -->
    <div class="bg-white rounded-lg shadow">
      <div class="overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-200">
          <thead class="bg-gray-50">
            <tr>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                问题摘要
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                用户
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                状态
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                提交时间
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                操作
              </th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <tr v-if="isLoading" class="text-center">
              <td colspan="5" class="px-6 py-12 text-gray-500">
                <div class="flex flex-col items-center">
                  <i class="fas fa-spinner fa-spin text-2xl text-blue-500 mb-4"></i>
                  <p class="text-lg font-medium">正在加载反馈列表...</p>
                </div>
              </td>
            </tr>
            <tr v-else-if="feedbacks.length === 0" class="text-center">
              <td colspan="5" class="px-6 py-12 text-gray-500">
                <div class="flex flex-col items-center">
                  <i class="fas fa-question-circle text-4xl text-gray-300 mb-4"></i>
                  <p class="text-lg font-medium">暂无反馈记录</p>
                  <p class="text-sm">您还没有提交过任何问题反馈</p>
                </div>
              </td>
            </tr>
            <tr v-for="feedback in feedbacks" :key="feedback.id" class="hover:bg-gray-50">
              <td class="px-6 py-4 whitespace-nowrap">
                <div class="text-sm font-medium text-gray-900">
                  {{ feedback.questionSummary }}
                </div>
              </td>
              <td class="px-6 py-4 whitespace-nowrap">
                <div class="text-sm text-gray-900">{{ feedback.displayName || feedback.username }}</div>
              </td>
              <td class="px-6 py-4 whitespace-nowrap">
                <span
                  :class="[
                    'inline-flex px-2 py-1 text-xs font-semibold rounded-full',
                    feedback.status === 1
                      ? 'bg-green-100 text-green-800'
                      : 'bg-yellow-100 text-yellow-800'
                  ]"
                >
                  {{ getStatusText(feedback.status) }}
                </span>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {{ formatDate(feedback.createdAt) }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">
                <button
                  @click="viewFeedback(feedback)"
                  class="text-blue-600 hover:text-blue-900 mr-3"
                >
                  查看
                </button>
                
                <!-- 普通用户操作 -->
                <button
                  v-if="!isAdmin && feedback.status === 0 && !feedback.adminFeedback"
                  @click="withdrawFeedback(feedback)"
                  class="text-red-600 hover:text-red-900 mr-3"
                >
                  撤回
                </button>
                
                <!-- 管理员操作 -->
                <button
                  v-if="isAdmin && feedback.status === 0"
                  @click="replyFeedback(feedback)"
                  class="text-green-600 hover:text-green-900 mr-3"
                >
                  回复
                </button>
                
                <!-- 超级管理员操作 -->
                <button
                  v-if="isSuperAdmin && feedback.replyType === 1 && feedback.status === 5"
                  @click="approveFeedback(feedback)"
                  class="text-purple-600 hover:text-purple-900 mr-3"
                >
                  审批
                </button>
                <button
                  v-if="isSuperAdmin && feedback.replyType === 1 && feedback.status === 5"
                  @click="openRejectDialog(feedback)"
                  class="text-red-600 hover:text-red-900"
                >
                  退回
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- 分页 -->
      <div class="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
        <div class="flex-1 flex justify-between sm:hidden">
          <button
            @click="previousPage"
            :disabled="currentPage === 0"
            class="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed"
          >
            上一页
          </button>
          <button
            @click="nextPage"
            :disabled="!hasNextPage"
            class="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed"
          >
            下一页
          </button>
        </div>
        <div class="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
          <div>
            <p class="text-sm text-gray-700">
              显示第 {{ (currentPage * pageSize) + 1 }} 到 {{ Math.min((currentPage + 1) * pageSize, totalElements) }} 条，
              共 {{ totalElements }} 条记录
            </p>
          </div>
          <div>
            <nav class="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
              <button
                @click="previousPage"
                :disabled="currentPage === 0"
                class="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                <i class="fas fa-chevron-left"></i>
              </button>
              <button
                @click="nextPage"
                :disabled="!hasNextPage"
                class="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                <i class="fas fa-chevron-right"></i>
              </button>
            </nav>
          </div>
        </div>
      </div>
    </div>

    <!-- 查看反馈详情弹窗 -->
    <div v-if="showViewDialog && selectedFeedback" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white rounded-lg p-6 w-full max-w-4xl mx-4 max-h-[80vh] overflow-y-auto">
        <div class="flex justify-between items-center mb-4">
          <h3 class="text-lg font-semibold text-gray-900">反馈详情</h3>
          <button
            @click="closeViewDialog"
            class="text-gray-400 hover:text-gray-600"
          >
            <i class="fas fa-times text-xl"></i>
          </button>
        </div>

        <div class="space-y-4">
          <!-- 用户信息 -->
          <div class="bg-gray-50 p-4 rounded-lg">
            <h4 class="font-medium text-gray-900 mb-2">用户信息</h4>
            <p class="text-sm text-gray-600">
              <strong>用户名：</strong>{{ selectedFeedback.displayName || selectedFeedback.username }}
            </p>
            <p class="text-sm text-gray-600">
              <strong>提交时间：</strong>{{ formatDate(selectedFeedback.createdAt) }}
            </p>
            <p class="text-sm text-gray-600">
              <strong>状态：</strong>
              <span
                :class="[
                  'inline-flex px-2 py-1 text-xs font-semibold rounded-full ml-2',
                  selectedFeedback.status === 1
                    ? 'bg-green-100 text-green-800'
                    : 'bg-yellow-100 text-yellow-800'
                ]"
              >
                {{ getStatusText(selectedFeedback.status) }}
              </span>
            </p>
          </div>

          <!-- 问题内容 -->
          <div class="bg-blue-50 p-4 rounded-lg">
            <h4 class="font-medium text-gray-900 mb-2">用户问题</h4>
            <div class="text-sm text-gray-700 whitespace-pre-wrap" v-html="selectedFeedback.questionContent"></div>
          </div>

          <!-- AI回答 -->
          <div class="bg-green-50 p-4 rounded-lg">
            <h4 class="font-medium text-gray-900 mb-2">AI回答</h4>
            <div class="text-sm text-gray-700 whitespace-pre-wrap" v-html="selectedFeedback.answerContent"></div>
          </div>

          <!-- 用户疑问 -->
          <div class="bg-yellow-50 p-4 rounded-lg">
            <h4 class="font-medium text-gray-900 mb-2">用户疑问</h4>
            <div class="text-sm text-gray-700 whitespace-pre-wrap" v-html="selectedFeedback.questionFeedback"></div>
          </div>

          <!-- 管理员回复 -->
          <div v-if="selectedFeedback.adminFeedback" class="bg-purple-50 p-4 rounded-lg">
            <h4 class="font-medium text-gray-900 mb-2">管理员回复</h4>
            <div class="text-sm text-gray-700 whitespace-pre-wrap" v-html="selectedFeedback.adminFeedback"></div>
          </div>

          <!-- 退回原因 -->
          <div v-if="selectedFeedback.rejectReason" class="bg-red-50 p-4 rounded-lg">
            <h4 class="font-medium text-gray-900 mb-2">退回原因</h4>
            <div class="text-sm text-gray-700 whitespace-pre-wrap" v-html="selectedFeedback.rejectReason"></div>
          </div>

          <!-- 管理员操作按钮 -->
          <div v-if="selectedFeedback.status === 5 && isSuperAdmin" class="flex justify-end space-x-3">
            <button
              @click="replyFeedback(selectedFeedback)"
              class="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors duration-200"
            >
              <i class="fas fa-reply mr-2"></i>回复反馈
            </button>
            <button
              @click="approveFeedback(selectedFeedback)"
              class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors duration-200"
            >
              <i class="fas fa-check mr-2"></i>审批通过
            </button>
            <button
              @click="openRejectDialog(selectedFeedback)"
              class="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors duration-200"
            >
              <i class="fas fa-times mr-2"></i>退回
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- 回复反馈弹窗 -->
    <div v-if="showReplyDialog && selectedFeedback" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" @click.self="forceCloseReplyDialog">
      <div class="bg-white rounded-lg p-6 w-full max-w-2xl mx-4" @click.stop>
        <div class="flex justify-between items-center mb-4">
          <h3 class="text-lg font-semibold text-gray-900">回复反馈</h3>
          <button
            @click="forceCloseReplyDialog"
            class="text-gray-400 hover:text-gray-600 p-1"
            title="关闭"
            :disabled="isSubmittingReply"
          >
            <i class="fas fa-times text-xl"></i>
          </button>
        </div>

        <div class="space-y-4">
          <!-- 完整问答内容 -->
          <div class="bg-blue-50 p-4 rounded-lg">
            <h4 class="font-medium text-gray-900 mb-2">用户问题</h4>
            <div class="text-sm text-gray-700 whitespace-pre-wrap" v-html="selectedFeedback.questionContent"></div>
          </div>
          
          <div class="bg-green-50 p-4 rounded-lg">
            <h4 class="font-medium text-gray-900 mb-2">AI回答</h4>
            <div class="text-sm text-gray-700 whitespace-pre-wrap" v-html="selectedFeedback.answerContent"></div>
          </div>
          
          <div class="bg-yellow-50 p-4 rounded-lg">
            <h4 class="font-medium text-gray-900 mb-2">用户反馈</h4>
            <div class="text-sm text-gray-700 whitespace-pre-wrap" v-html="selectedFeedback.questionFeedback"></div>
          </div>

          <!-- 回复类型选择 -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">
              回复类型：
            </label>
            <div class="space-y-2">
              <label class="flex items-center">
                <input
                  v-model="replyType"
                  type="radio"
                  :value="1"
                  class="mr-2"
                />
                <span class="text-sm text-gray-700">反馈知识库（需要超级管理员审批）</span>
              </label>
              <label class="flex items-center">
                <input
                  v-model="replyType"
                  type="radio"
                  :value="2"
                  class="mr-2"
                />
                <span class="text-sm text-gray-700">仅反馈用户（直接完成）</span>
              </label>
            </div>
          </div>

          <!-- 回复内容 -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">
              管理员回复：
            </label>
            <textarea
              v-model="replyContent"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              rows="6"
              placeholder="请输入您的回复..."
            ></textarea>
          </div>

          <!-- 按钮组 -->
          <div class="flex justify-end space-x-3">
            <button
              @click="forceCloseReplyDialog"
              class="px-4 py-2 text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors duration-200"
              :disabled="isSubmittingReply"
            >
              取消
            </button>
            <button
              @click="submitReply"
              :disabled="isSubmittingReply || !replyContent.trim()"
              class="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors duration-200"
            >
              {{ isSubmittingReply ? '提交中...' : '确定回复' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- 退回反馈弹窗 -->
    <div v-if="showRejectDialog && selectedFeedback" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" @click.self="closeRejectDialog">
      <div class="bg-white rounded-lg p-6 w-full max-w-lg mx-4" @click.stop>
        <div class="flex justify-between items-center mb-4">
          <h3 class="text-lg font-semibold text-gray-900">退回反馈</h3>
          <button
            @click="closeRejectDialog"
            class="text-gray-400 hover:text-gray-600 p-1"
            title="关闭"
          >
            <i class="fas fa-times text-xl"></i>
          </button>
        </div>

        <div class="space-y-4">
          <!-- 反馈信息 -->
          <div class="bg-gray-50 p-4 rounded-lg">
            <h4 class="font-medium text-gray-900 mb-2">反馈信息</h4>
            <p class="text-sm text-gray-600">
              <strong>用户：</strong>{{ selectedFeedback.displayName || selectedFeedback.username }}
            </p>
            <p class="text-sm text-gray-600">
              <strong>问题：</strong>{{ selectedFeedback.questionSummary }}
            </p>
          </div>

          <!-- 退回原因输入 -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">
              退回原因 <span class="text-red-500">*</span>
            </label>
            <textarea
              v-model="rejectReason"
              rows="4"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
              placeholder="请输入退回原因..."
            ></textarea>
          </div>

          <!-- 按钮组 -->
          <div class="flex justify-end space-x-3">
            <button
              @click="closeRejectDialog"
              class="px-4 py-2 text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors duration-200"
            >
              取消
            </button>
            <button
              @click="submitReject"
              :disabled="!rejectReason.trim()"
              class="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors duration-200"
            >
              确定退回
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'FeedbackModule',
  data() {
    return {
      feedbacks: [],
      totalFeedbacks: 0,
      unrepliedCount: 0,
      repliedCount: 0,
      currentPage: 0,
      pageSize: 10,
      totalElements: 0,
      hasNextPage: false,
      searchKeyword: '',
      statusFilter: '',
      showViewDialog: false,
      showReplyDialog: false,
      showRejectDialog: false,
      selectedFeedback: null,
      replyContent: '',
      replyType: 1, // 1-反馈知识库，2-仅反馈用户
      rejectReason: '',
      isSubmittingReply: false,
      isLoading: false,
      userRole: '',
      isAdmin: false,
      isSuperAdmin: false
    }
  },
  mounted() {
    this.loadUserRole();
    this.loadFeedbacks();
    this.loadStatistics();
  },
  beforeDestroy() {
    // 确保在组件销毁时重置所有状态
    this.isSubmittingReply = false;
    this.isLoading = false;
  },
  methods: {
    // 加载用户角色
    loadUserRole() {
      this.userRole = localStorage.getItem('userRole') || '';
      this.isAdmin = this.userRole === 'admin' || this.userRole === 'user_manager';
      this.isSuperAdmin = this.userRole === 'admin';
    },

    // 加载反馈列表
    async loadFeedbacks() {
      try {
        this.isLoading = true;
        const token = localStorage.getItem('token');
        if (!token) {
          this.$router.push('/login');
          return;
        }

        let url = '';
        if (this.isAdmin) {
          // 管理员和用户管理员：查看所有反馈
          url = `${process.env.VUE_APP_API_BASE_URL}/api/feedback/all-feedbacks?page=${this.currentPage}&size=${this.pageSize}`;
        } else {
          // 普通用户：只查看自己的反馈
          url = `${process.env.VUE_APP_API_BASE_URL}/api/feedback/user-feedbacks?page=${this.currentPage}&size=${this.pageSize}`;
        }
        
        // 添加状态筛选参数
        if (this.statusFilter !== '') {
          url += `&status=${this.statusFilter}`;
        }
        
        // 添加搜索关键词参数
        if (this.searchKeyword.trim() !== '') {
          url += `&keyword=${encodeURIComponent(this.searchKeyword.trim())}`;
        }

        console.log('加载反馈列表，URL:', url);
        console.log('用户角色:', this.userRole, 'isSuperAdmin:', this.isSuperAdmin);

        const response = await fetch(url, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        const result = await response.json();
        console.log('反馈列表API响应:', result);
        
        if (result.code === 200) {
          this.feedbacks = result.data.content || [];
          this.totalElements = result.data.totalElements || 0;
          this.hasNextPage = !result.data.last;
          console.log('加载到反馈数量:', this.feedbacks.length);
        } else {
          console.error('加载反馈列表失败:', result.message);
          this.feedbacks = [];
          this.totalElements = 0;
        }
      } catch (error) {
        console.error('加载反馈列表失败:', error);
        this.feedbacks = [];
        this.totalElements = 0;
      } finally {
        this.isLoading = false;
      }
    },

    // 加载统计数据
    async loadStatistics() {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;

        // 获取未处理数量
        const unrepliedResponse = await fetch(`${process.env.VUE_APP_API_BASE_URL}/api/feedback/unreplied-count`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        const unrepliedResult = await unrepliedResponse.json();
        if (unrepliedResult.code === 200) {
          this.unrepliedCount = unrepliedResult.data.totalUnreplied;
        }

        // 获取已处理数量
        const repliedResponse = await fetch(`${process.env.VUE_APP_API_BASE_URL}/api/feedback/replied-count`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        const repliedResult = await repliedResponse.json();
        if (repliedResult.code === 200) {
          this.repliedCount = repliedResult.data.totalReplied;
        }

        // 计算总数量
        this.totalFeedbacks = this.unrepliedCount + this.repliedCount;
      } catch (error) {
        console.error('加载统计数据失败:', error);
      }
    },

    // 查看反馈详情
    viewFeedback(feedback) {
      this.selectedFeedback = feedback;
      this.showViewDialog = true;
    },

    // 关闭查看弹窗
    closeViewDialog() {
      this.showViewDialog = false;
      this.selectedFeedback = null;
    },

    // 回复反馈
    replyFeedback(feedback) {
      console.log('打开回复弹窗，反馈数据:', feedback);
      this.selectedFeedback = feedback;
      this.replyContent = '';
      this.showReplyDialog = true;
    },

    // 关闭回复弹窗
    closeReplyDialog() {
      console.log('关闭回复弹窗');
      this.showReplyDialog = false;
      this.selectedFeedback = null;
      this.replyContent = '';
      this.replyType = 1;
      // 强制重置提交状态，防止卡住
      this.isSubmittingReply = false;
    },

    // 强制关闭回复弹窗
    forceCloseReplyDialog() {
      console.log('强制关闭回复弹窗');
      this.showReplyDialog = false;
      this.selectedFeedback = null;
      this.replyContent = '';
      this.replyType = 1;
      this.isSubmittingReply = false;
      // 强制更新视图
      this.$forceUpdate();
    },

    // 提交回复
    async submitReply() {
      if (!this.replyContent.trim()) {
        alert('请输入回复内容');
        return;
      }

      try {
        console.log('开始提交回复，isSubmittingReply设置为true');
        this.isSubmittingReply = true;
        const token = localStorage.getItem('token');
        if (!token) {
          console.log('用户未登录，跳转到登录页面');
          this.$router.push('/login');
          return;
        }

        console.log('发送回复请求到:', `${process.env.VUE_APP_API_BASE_URL}/api/feedback/${this.selectedFeedback.id}/reply`);
        
        // 创建超时控制器
        const controller = new AbortController();
        const timeoutId = setTimeout(() => {
          controller.abort();
        }, 30000); // 30秒超时
        
        const response = await fetch(`${process.env.VUE_APP_API_BASE_URL}/api/feedback/${this.selectedFeedback.id}/reply`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            adminFeedback: this.replyContent,
            replyType: this.replyType
          }),
          signal: controller.signal
        });
        
        clearTimeout(timeoutId);

        console.log('收到回复响应，状态码:', response.status);
        const result = await response.json();
        console.log('回复响应数据:', result);
        
        if (result.code === 200) {
          alert('回复成功');
          console.log('回复成功，开始关闭弹窗和刷新数据');
          // 强制关闭弹窗
          this.forceCloseReplyDialog();
          // 然后刷新数据
          try {
            await this.loadFeedbacks();
            await this.loadStatistics();
            console.log('数据刷新完成');
          } catch (refreshError) {
            console.error('刷新数据时出错:', refreshError);
            // 即使刷新失败也不影响回复成功
          }
        } else {
          console.log('回复失败，错误信息:', result.message);
          alert(result.message || '回复失败');
        }
      } catch (error) {
        console.error('回复过程中发生错误:', error);
        alert('回复失败，请重试: ' + error.message);
      } finally {
        console.log('重置isSubmittingReply为false');
        this.isSubmittingReply = false;
        // 强制关闭弹窗，无论什么情况
        this.forceCloseReplyDialog();
      }
    },

    // 搜索反馈
    searchFeedbacks() {
      // 这里可以实现搜索逻辑
      this.currentPage = 0;
      this.loadFeedbacks();
    },

    // 按状态筛选
    filterByStatus() {
      this.currentPage = 0;
      this.loadFeedbacks();
    },

    // 刷新反馈列表
    refreshFeedbacks() {
      this.currentPage = 0;
      this.loadFeedbacks();
      this.loadStatistics();
    },

    // 上一页
    previousPage() {
      if (this.currentPage > 0) {
        this.currentPage--;
        this.loadFeedbacks();
      }
    },

    // 下一页
    nextPage() {
      if (this.hasNextPage) {
        this.currentPage++;
        this.loadFeedbacks();
      }
    },

    // 撤回反馈
    async withdrawFeedback(feedback) {
      if (!confirm('确定要撤回这个反馈吗？')) {
        return;
      }

      try {
        const token = localStorage.getItem('token');
        if (!token) {
          this.$router.push('/login');
          return;
        }

        const response = await fetch(`${process.env.VUE_APP_API_BASE_URL}/api/feedback/${feedback.id}/withdraw`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        const result = await response.json();
        if (result.code === 200) {
          alert('撤回成功');
          this.loadFeedbacks();
          this.loadStatistics();
        } else {
          alert(result.message || '撤回失败');
        }
      } catch (error) {
        console.error('撤回失败:', error);
        alert('撤回失败，请重试');
      }
    },

    // 审批反馈
    async approveFeedback(feedback) {
      if (!confirm('确定要通过这个反馈吗？')) {
        return;
      }

      try {
        const token = localStorage.getItem('token');
        if (!token) {
          this.$router.push('/login');
          return;
        }

        const response = await fetch(`${process.env.VUE_APP_API_BASE_URL}/api/feedback/${feedback.id}/approve`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        const result = await response.json();
        if (result.code === 200) {
          alert('审批成功');
          this.loadFeedbacks();
          this.loadStatistics();
        } else {
          alert(result.message || '审批失败');
        }
      } catch (error) {
        console.error('审批失败:', error);
        alert('审批失败，请重试');
      }
    },

    // 显示退回弹窗
    openRejectDialog(feedback) {
      this.selectedFeedback = feedback;
      this.rejectReason = '';
      this.showRejectDialog = true;
    },

    // 关闭退回弹窗
    closeRejectDialog() {
      this.showRejectDialog = false;
      this.selectedFeedback = null;
      this.rejectReason = '';
    },

    // 提交退回
    async submitReject() {
      if (!this.rejectReason.trim()) {
        alert('请输入退回原因');
        return;
      }

      if (!confirm('确定要退回这个反馈吗？')) {
        return;
      }

      try {
        const token = localStorage.getItem('token');
        if (!token) {
          this.$router.push('/login');
          return;
        }

        const response = await fetch(`${process.env.VUE_APP_API_BASE_URL}/api/feedback/${this.selectedFeedback.id}/reject`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            rejectReason: this.rejectReason
          })
        });

        const result = await response.json();
        if (result.code === 200) {
          alert('退回成功');
          this.closeRejectDialog();
          this.loadFeedbacks();
          this.loadStatistics();
        } else {
          alert(result.message || '退回失败');
        }
      } catch (error) {
        console.error('退回失败:', error);
        alert('退回失败，请重试');
      }
    },

    // 格式化日期
    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleString('zh-CN');
    },

    // 获取状态文本
    getStatusText(status) {
      switch (status) {
        case 0:
          return '未处理';
        case 1:
          return '已处理';
        case 2:
          return '仅反馈用户';
        case 3:
          return '已撤回';
        case 4:
          return '已退回';
        case 5:
          return '已反馈知识库';
        default:
          return '未知状态';
      }
    }
  }
}
</script>

<style scoped>
.feedback-module {
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
}
</style>
